<template>
  <zm-alert
    v-if="activeBannerConfig"
    class="upsell-biz-banner"
    :title="$t('billing.zone.upsell_biz_banner.title')"
    type="success"
    :closable="true"
    :show-icon="false"
    @close="handleClose"
  >
    <span class="upsell-biz-banner__desc">{{ bannerContent }}</span>
    <zm-button @click="handleSwitchToBusiness" type="link">
      {{ bannerButtonText }}
    </zm-button>
  </zm-alert>
</template>

<script>
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { flowTypes } from '@/common/service/page-config';
import { isEmptyVal } from '@/utils';
import Migration from '@/plans/ZOOM_MEETING/dialog/mixins/migration';
import { plansof } from '@/utils/chain';
import { couponSourceTypeMap } from '../coupon/helper/constant';

// If banner content is empty, return the cached result. 
// To fix: when just switch to biz a moment, proPlanInCart is null, the content function returns ''.
const memorize = (fn) => {
  let cache = ''
  return () => {
    const result = fn()
    if (!result) {
      return cache
    }
    cache = result
    return result
  }
}

export default {
  name: 'UpsellBanner',
  mixins: [Migration],
  data() {
    return {
      isDismissed: false,
      activeBannerConfig: null,
      debounceTimer: null,
      bannerConfig: [
        // pro to biz
        {
          shouldShow: () => {
            if (this.isDismissed) return false;
            if (this.$context.is.isFreeWinback) return false;
            if (this.$context.flow !== flowTypes.buyNow && this.$context.flow !== flowTypes.basic2pro) return false;
            if (!isEmptyVal(this.partnerRefer) && !this.forNewSubscriptionFlow) return false;
            if (this.discountWithAmountMap?.totalDiscount > 0 || this.planTotalDiscount > 0 || this.couponState.couponSourceType === couponSourceTypeMap.threshold) return false;
            if (this.verifyCouponStatusLoading && !this.activeBannerConfig) return false 

            const proPlanInCart = this.getSelectedPlans.find(
              (plan) => plan.type === planTypes.ZOOM_MEETING && plan.category === ZOOM_MEETING_SUB_TYPES.PRO,
            );
            if (proPlanInCart) {
              if (this.discountWithAmountMap?.discountPlans?.[proPlanInCart.plan_code]) {
                return false;
              }
              return true;
            }

            return false;
          },
          content: memorize(() => {
            const proPlanInCart = this.getSelectedPlans.find(
              (plan) => plan.type === planTypes.ZOOM_MEETING && plan.category === ZOOM_MEETING_SUB_TYPES.PRO,
            );
            if (!proPlanInCart) return ''
            let priceDiff = '';
            let billingCycle = proPlanInCart.billing_cycle;
            const bizBillingCycle =
              billingCycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY ? BILLING_CYCLE.ANNUAL : billingCycle;
            const bizPlanCandidate = plansof(this.planMapFlat)
              .type(planTypes.ZOOM_MEETING)
              .sub_type(ZOOM_MEETING_SUB_TYPES.BIZ)
              .cycle(bizBillingCycle)
              .get(0);
            if (billingCycle === BILLING_CYCLE.ANNUAL || billingCycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY) {
              priceDiff = (bizPlanCandidate.amount - proPlanInCart.amount) / 12;
            } else {
              priceDiff = bizPlanCandidate.amount - proPlanInCart.amount;
            }
            priceDiff = this.$money(priceDiff, { forceDiff: true });
            return this.$t('billing.zone.upsell_biz_banner.desc', { price: priceDiff });
          }),
          buttonText: () => {
            return this.$t('billing.zone.upsell_biz_banner.btn');
          },
          onClose: () => {
            this.$tracking.buyInteraction(
              'banner - product level',
              `zoom workplace pro - need more tools for your business - close`,
            );
          },
          onSwitch: async () => {
            this.$tracking.buyInteraction(
              'banner - product level',
              `zoom workplace pro - need more tools for your business - switch to business`,
            );
            if (this.healthcareAvailable) {
              try {
                await this.handleConfirmHealthcareSwitchPlan();
              } catch (err) {
                return;
              }
            }
            await this.switchMeetingPlanCategory({ category: ZOOM_MEETING_SUB_TYPES.BIZ, btn: { disabled: false } });
          },
          onShow: () => {
            this.$tracking.buyInteraction(
              'banner - product level',
              `zoom workplace pro - need more tools for your business - impression`,
            );
          },
        },
        // pro plus to biz plus
        {
          shouldShow: () => {
            if (this.isDismissed) return false;
            if (this.$context.is.isFreeWinback) return false;
            if (this.$context.flow !== flowTypes.buyNow && this.$context.flow !== flowTypes.basic2pro) return false;
            if (!isEmptyVal(this.partnerRefer) && !this.forNewSubscriptionFlow) return false;
            if (this.discountWithAmountMap?.totalDiscount > 0 || this.planTotalDiscount > 0 || this.couponState.couponSourceType === couponSourceTypeMap.threshold) return false;
            if (this.verifyCouponStatusLoading && !this.activeBannerConfig) return false 

            const proPlusPlanInCart = this.getSelectedPlans.find(
              (plan) =>
                plan.type === planTypes.ZONE_PRO_PLUS && plan.sub_type === ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS,
            );
            if (proPlusPlanInCart) {
              if (this.discountWithAmountMap?.discountPlans?.[proPlusPlanInCart.plan_code]) {
                return false;
              }
              return true;
            }

            return false;
          },
          content: memorize(() => {
            let priceDiff = 0;
            const proPlusPlanInCart = this.getSelectedPlans.find(
              (plan) =>
                plan.type === planTypes.ZONE_PRO_PLUS && plan.sub_type === ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS,
            );
            if (!proPlusPlanInCart) return ''
            let billingCycle = proPlusPlanInCart.billing_cycle;
            const bizPlanCandidate = plansof(this.planMapFlat)
              .type(planTypes.ZOOM_ONE_BUNDLE)
              .sub_type(ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS)
              .cycle(billingCycle)
              .get(0);
            if (billingCycle === BILLING_CYCLE.ANNUAL) {
              priceDiff = (bizPlanCandidate.amount - proPlusPlanInCart.amount) / 12;
            } else {
              priceDiff = bizPlanCandidate.amount - proPlusPlanInCart.amount;
            }
            priceDiff = this.$money(priceDiff, { forceDiff: true });
            return this.$t('billing.zone.upsell_bizplus_banner.desc', { price: priceDiff });
          }),
          buttonText: () => {
            return this.$t('billing.zone.upsell_bizplus_banner.btn');
          },
          onClose: () => {
            this.$tracking.buyInteraction(
              'banner - product level',
              `zoom workplace pro plus - need more tools for your business - close`,
            );
          },
          onSwitch: async () => {
            this.$tracking.buyInteraction(
              'banner - product level',
              `zoom workplace pro plus - need more tools for your business - switch to business`,
            );
            await this.switchProBizUrl({
              category: ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS,
              btn: { disabled: false },
            }, true);
          },
          onShow: () => {
            this.$tracking.buyInteraction(
              'banner - product level',
              `zoom workplace pro plus - need more tools for your business - impression`,
            );
          },
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'planMapFlat',
      'partnerRefer',
      'forNewSubscriptionFlow',
      'limitLicenseWithBaseZoneTypes',
      'healthcareAvailable',
      'getCurrentBasePlanInfo',
      'discountWithAmountMap',
      'planTotalDiscount',
      'verifyCouponStatusLoading',
      'couponState'
    ]),
    computedBannerConfig() {
      for (const config of this.bannerConfig) {
        if (config.shouldShow()) {
          return config;
        }
      }
      return null;
    },
    bannerContent() {
      return this.activeBannerConfig && this.activeBannerConfig.content();
    },
    bannerButtonText() {
      return this.activeBannerConfig && this.activeBannerConfig.buttonText();
    },
    currentBaseCategory() {
      const { planType, plans } = this.getCurrentBasePlanInfo;
      if (planType !== planTypes.ZOOM_MEETING || !plans[0]) {
        return '';
      }
      return get(plans, '0.zoomMeetingCategory', '');
    },
  },
  watch: {
    computedBannerConfig: {
      handler(newval, oldval) {
        const setBanner = () => {
          this.activeBannerConfig = newval;
          if (newval && (newval !== oldval)) {
            newval.onShow();
          }
        }
        // If it's the first time to set the banner, set it immediately
        if (typeof oldval === 'undefined') {
          setBanner();
        }
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        
        this.debounceTimer = setTimeout(() => {
          setBanner();
        }, 500);
      },
      immediate: true,
    },
  },
  methods: {
    handleClose() {
      this.isDismissed = true;
      if (this.activeBannerConfig) {
        this.activeBannerConfig.onClose();
      }
    },
    async handleSwitchToBusiness() {
      if (this.activeBannerConfig) {
        await this.activeBannerConfig.onSwitch();
      }
    },
    handleSure() {},
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
</script>
<style lang="scss" scoped>
.upsell-biz-banner {
  margin-bottom: 16px;
  &__desc {
    margin-right: 8px;
  }

  ::v-deep {
    .zm-alert {
      &__body {
        padding: 0;
        line-height: 18px;
      }
    }
    .zm-button {
      padding: 0;
    }
  }
}
</style>
